import { useLocation } from "zudoku/router";
import blogPosts from "../config/blogPosts";
import { authorFor, authorPhoto, authorUrl } from "../config/authors";

/**
 * PostByline — who wrote this post, above the article body.
 *
 * Mounted on Zudoku's `content-before` slot (zudoku.config.tsx), which renders
 * inside Main, directly above page content. It is the visible counterpart to
 * SeoHead: that file states the author to a crawler in Article JSON-LD, this one
 * states it to a reader, and both resolve the same person from the same registry
 * so they cannot disagree.
 *
 * BLOG POSTS ONLY. The slot fires on every page that goes through Layout, so
 * this no-ops everywhere else — docs pages have no author (docsNav.json carries
 * labels only) and the API playground has none either. Same scoping SeoHead uses,
 * and for the same reason.
 *
 * The blog INDEX is not covered here: it is a custom-page with layout:"none", so
 * layout slots never reach it. Its per-card byline lives in BlogHome.tsx.
 *
 * The link goes to the marketing site's /about/<slug> — a page about exactly one
 * person, which is what lets the author entity resolve. An anchor on /about would
 * be the same document to a crawler and would resolve to nothing of its own.
 */
export default function PostByline() {
  const { pathname } = useLocation();

  // pathname is basePath-relative inside the app ("/blogs/foo", not
  // "/pub/blogs/foo") — react-router strips the basename. Same note as SeoHead.
  const match = /^\/blogs\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;

  const post = blogPosts.find((p) => p.slug === match[1]);
  if (!post) return null;

  // `author` is absent from posts generated before the field existed;
  // authorFor falls back to the default rather than rendering a broken byline.
  const author = authorFor((post as { author?: string }).author);

  return (
    <div className="post-byline">
      <a className="post-byline__link" href={authorUrl(author)}>
        <img
          className="post-byline__avatar"
          src={authorPhoto(author)}
          alt=""
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
        />
        <span className="post-byline__text">
          <span className="post-byline__name">{author.name}</span>
          <span className="post-byline__meta">
            {author.role}
            {post.date ? ` · ${post.date}` : ""}
          </span>
        </span>
      </a>
    </div>
  );
}
